import { StatusSwitch } from '@app/modules/admin/components/permission/StatusSwitch';
import {
  UsersAuthzGroupPermissionDto,
  useUsersAuthzAllowPermissionToGroupManagedPermissions,
  useUsersAuthzDenyPermissionToGroupManagedPermissions,
} from '@mi-front/api-v2';
import { Box, Card, CardContent } from '@mui/material';
import { useState } from 'react';
import { useLanguage } from '../../../../core/hooks/useLanguage';
import { PermissionItemComponentProps } from './ActionPermissionsView';

interface Props extends PermissionItemComponentProps<UsersAuthzGroupPermissionDto> {
  readonly groupId: string;
}

/**
 * Returns the localized string for the current UI language from a text map.
 */
export function useLocalizedText(textMap: object) {
  const lang = useLanguage() as keyof typeof textMap;

  return textMap[lang];
}

/**
 * Renders allow/deny controls for a single group managed permission.
 */
export function GroupPermissionItem(p: Props) {
  const { groupId, permissionState: initialGroupPermission, permissionDefinition } = p;

  const [groupPermission, setGroupPermission] = useState(initialGroupPermission);

  const mutationParams = {
    groupId,
    permission: groupPermission.permission,
  };

  const allowMutation = useUsersAuthzAllowPermissionToGroupManagedPermissions({
    mutation: {
      onSuccess: (data: UsersAuthzGroupPermissionDto) => {
        setGroupPermission(data);
      },
    },
  });

  const denyMutation = useUsersAuthzDenyPermissionToGroupManagedPermissions({
    mutation: {
      onSuccess: (data: UsersAuthzGroupPermissionDto) => {
        setGroupPermission(data);
      },
    },
  });

  const isUpdating = allowMutation.isPending || denyMutation.isPending;

  return (
    <Card>
      <CardContent>
        <Box mb={1}>{useLocalizedText(permissionDefinition.description)}</Box>
        <Box sx={{ marginRight: 3 }}>
          <StatusSwitch
            checked={groupPermission.allowed}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              if (event.target.checked) {
                allowMutation.mutate(mutationParams);
              } else {
                denyMutation.mutate(mutationParams);
              }
            }}
            disabled={isUpdating}
            sx={{ marginBottom: 0 }}
          />
        </Box>
      </CardContent>
    </Card>
  );
}
